import { PlaneCanvas } from "@firesim/simkit-core";
import { Plane } from "@firesim/simkit-core";
import { FireCell, isFireCellProps } from "@firesim/simkit-core";
import { setupControls } from "@firesim/simkit-core";
import { setupInstructionDialog, showInstructions } from "@firesim/simkit-core";
//import setupIntroDialog from '../core/controls/close-intro';
import { bindLoadButton } from "./loadRuralBlockData";
import { SceneCreator } from "@firesim/simkit-core";
import type { SceneTerrain } from "@firesim/simkit-core";
//import setupCounter from './counter';
import { hideHouseBurnedWarning, enableWarning } from "./house-burned-warning";
//import browserUpdate from 'browser-update';
//import {growSpinifex, SpinifexType, isSpinifexType} from './grow-spinifex';

import { supportsCSSTransformsOnSVG } from "@firesim/simkit-core";
import { WindDirections } from "@firesim/simkit-core";
import { calcWindChange } from "@firesim/simkit-core";
//import {calcThrowDistance} from './wind-influence';
import { getFireState, FireDanger, WindSpeed, FireStateAttributes, isFireDanger } from "@firesim/simkit-core";
import { Controls } from "@firesim/simkit-core";
//import {setupDrawButton} from '../core/controls/draw-type';
import { setupPlaceIgnitionButton } from "./draw-ignition";
import { isButtonElement } from "@firesim/simkit-core";
import { enableAdminMode } from "@firesim/simkit-core";
import type { Config } from "./config";
import { TerrainCodes, terrain, terrainCodeValues, terrainTypes } from "./terrain-types";
import { setupSpeedControls } from "@firesim/simkit-core";

// These polyfills drastically increase the build size
// They should be reviewed when IE11 support is no longer required
import "@babel/polyfill";
import "whatwg-fetch";
// Polyfill moved to simkit-core

import { createImageMask } from "@firesim/simkit-core";
import { CellProps } from "@firesim/simkit-core";
import { addCellInspectorToggle } from "@firesim/simkit-core";
import { setupDrawButton } from "@firesim/simkit-core";
import { getBoolFromParams } from "@firesim/simkit-core";
import { getStringFromParams } from "@firesim/simkit-core";
import { setupCounter } from "@firesim/simkit-core";
import { exportTerrainGrid } from "@firesim/simkit-core";

/*
const drawImageShape = async (plane: Plane) => {
    const data = await createImageMask('images/grass.png', 100, 100);
    console.log('Image', data);

    data.forEach((row, y) => {
        row.forEach((isMasked, x) => {
            if(isMasked) {
                plane.drawCell(x, y, 255, 255, 255, 250);
            }
        });
    });
};
*/

export function setupBlock(config: Config) {
  document.addEventListener("DOMContentLoaded", async () => {
    if (!supportsCSSTransformsOnSVG()) {
      const controls = document.querySelector(".controls");
      if (controls) {
        controls.classList.remove("svg-supported");
      }
    }

    //browserUpdate({required: {e: 44}});

    // Setup plane
    const firecellFactory = (props: CellProps<TerrainCodes, FireCell<TerrainCodes>>) => {
        if(isFireCellProps(props)) {
            return new FireCell(props);
        }
        return new FireCell({
            fuelLoad: 0,
            ...props
        });
    }

    const sceneCreator = new SceneCreator<FireCell<TerrainCodes>, TerrainCodes>(
      firecellFactory,
    );
    const cellData = await sceneCreator.loadTerrainFromJson(config.planeData, terrainCodeValues);
    const cells = sceneCreator.loadCellsFromData(cellData, terrainTypes);

    const canvas = document.getElementById("canvas") as HTMLCanvasElement;

    const layers = [
      "background",
      "sprites",
      "main",
      "buildings",
      "fire",
      "effects",
    ];
    const canvasDrawer = new PlaneCanvas(canvas, { layers, cellScale: config.scale || 1 });
    const plane = new Plane<TerrainCodes, FireCell<TerrainCodes>>(
      cells,
      canvasDrawer,
    );

    plane.agentRunOrder = ["FrontAgent", "EmberAgent"];

    const controls = new Controls(plane);

    const state = getFireState(plane);
    state.setCompassInverted(true);

    state.setWindDirection(WindDirections.N);

    plane.addTickPrerunner(() => {
      let currentWindSpeed = state.currentWindSpeed;
      let windSpeed = state.windSpeed;
      state.setCurrentWindSpeed(calcWindChange(currentWindSpeed, windSpeed));
      //state.setThrowDistance(calcThrowDistance(currentWindSpeed));
      state.setThrowDistance(0);
    });

    const sceneTerrain: SceneTerrain<TerrainCodes> = {
      buildings: terrainTypes[TerrainCodes.House],
      roads: terrainTypes[TerrainCodes.Road],
      trees: terrainTypes[TerrainCodes.ForestHigh],
      default: terrainTypes[TerrainCodes.GrassNatural],
    };

    cells.flat().forEach((cell) => cell.plane = plane);

    if(config.backgroundImage) {
        await sceneCreator.loadBackground({
            plane,
            imgSrc: config.backgroundImage,
            backgroundColor: config.backgroundColor,
            scaleCanvasToImage: true
        });
    }

    await Promise.all([
        sceneCreator.drawTerrain(config.buildings, plane),
        sceneCreator.drawTerrain(config.roads, plane),
        sceneCreator.drawTerrain(config.trees, plane),
        sceneCreator.drawTerrain(config.terrain, plane),
    ]);
    controls.fireDanger = FireDanger.Medium;
    controls.windSpeed = WindSpeed.Light;
    controls.windDirection = WindDirections.N;

    setupControls(plane);

    //setupIntroDialog();
    setupInstructionDialog();
    //plane.addEventListener("click", showInstructions);

    enableAdminMode(plane);

    setupDrawButton({
        terrain: terrainTypes[TerrainCodes.Dirt],
        selector: '#drawBreakBtn',
        radius: 2,
        coverage: 0.9,
        delayTime: 1,
        plane,
        excludeTerrain: [TerrainCodes.House, TerrainCodes.Road, TerrainCodes.Water, TerrainCodes.Dirt, TerrainCodes.ForestMed, TerrainCodes.ForestHigh]
    });
    setupDrawButton({
        terrain: terrainTypes[TerrainCodes.GrassNatural],
        selector: '#drawGrassBtn',
        radius: 6,
        coverage: 0.2,
        plane,
        excludeTerrain: [TerrainCodes.House, TerrainCodes.Road, TerrainCodes.Water, TerrainCodes.GrassNatural]
    });

    const ignitionBtn = document.getElementById("ignitionBtn");
    if (!isButtonElement(ignitionBtn)) {
      throw new Error("ignitionBtn is not a button element");
    }
    setupPlaceIgnitionButton(ignitionBtn, plane);

    setupSpeedControls({
        plane,
        selector: '#speedInput',
        baseSpeed: 500
    })
    bindLoadButton(
      document.getElementById("reloadBtn") as HTMLButtonElement,
      plane,
      () => {
        hideHouseBurnedWarning();
        enableWarning();
      },
    );

    const restartBtn = document.getElementById("restartBtn");
    if (restartBtn) {
      restartBtn.addEventListener("click", (evt) => {
        evt.preventDefault();
        window.location.reload();
      });
    }

    const updateBackgroundImage = async (value: FireDanger) => {
        let backgroundImage = "images/wheatbelt_background_green.jpg"
        if(value >= FireDanger.VeryHigh) {
            backgroundImage = "images/wheatbelt_background_yellow2.png";
        } else if(value >= FireDanger.High) {
            backgroundImage = "images/wheatbelt_background_yellow1.png";
        }
        await sceneCreator.loadBackground({
            plane,
            imgSrc: backgroundImage,
            backgroundColor: config.backgroundColor,
            scaleCanvasToImage: false
        });
    };
    state.bind(FireStateAttributes.FireDanger, updateBackgroundImage);
    updateBackgroundImage(state.fireDanger);

    if(getBoolFromParams('inspector')) {
        addCellInspectorToggle(plane);
    }

    setupCounter({
        textElement: document.getElementById('tickCounter') as HTMLElement,
        plane,
        mode: "minutes",
        tickQuota: 10
    });

    const exportLabel = getStringFromParams("exportTerrain");
    if (exportLabel) {
      exportTerrainGrid(plane, ['type', 'fuelLoad'], exportLabel);
    }
  });

}
